import React from "react"
import { SongVinyl } from "./song_vinyl"
import { BookCover } from "./book_cover"
import { BookSpine } from "./book_spine"
import { typeKey } from "./variant_config"
import type { MediaResource, MediaVariant, MediaPreviewOptions } from "./types"

// Pick the default treatment from the resource's type. Rich media is the default — songs/audio get the
// record & sleeve, codex records a labelled spine, everything else a book cover.
export const variantForResource = (resource: MediaResource): Exclude<MediaVariant, "auto"> => {
  const t = typeKey(resource)
  // Codex first — "codex_record" would otherwise match the audio "…record…" test below.
  if (t === "codex_record") return "spine"
  if (/song|music|audio|podcast|radio/.test(t)) return "sleeve"
  return "cover"
}

/**
 * The default rich-media preview for a resource, dispatching to the shared treatments (SongVinyl / BookCover
 * / BookSpine). `variant` defaults to "auto" (picked from the resource type); pass a specific variant to
 * force one across a section — the "layout option". All generated details (label/spine colour, disc tilt)
 * are seeded from the resource gid by the underlying components, so the look is stable per resource.
 *
 * ```tsx
 * <MediaPreview resource={song} />                    // auto -> record & sleeve
 * <MediaPreview resource={book} variant="cover" />    // force a cover
 * <MediaPreview resource={item} {...resolveMediaConfig({ item: item.display, section })} />
 * ```
 */
export const MediaPreview: React.FC<{ resource: MediaResource } & MediaPreviewOptions> = ({
  resource,
  variant = "auto",
  showImage = true,
  theme,
  className,
}) => {
  const resolved = variant === "auto" ? variantForResource(resource) : variant
  const name = resource.name || "Untitled"
  const cover = showImage ? resource.thumbnailUrl || resource.imageUrl : null

  switch (resolved) {
    case "vinyl":
    case "sleeve":
      return (
        <SongVinyl
          item={{ id: resource.id, name, path: resource.path, imageUrl: cover }}
          variant={resolved === "vinyl" ? "disc" : "sleeve"}
          theme={theme}
          className={className}
        />
      )
    case "spine":
      return <BookSpine name={name} subheader={resource.translation} theme={theme} className={className} />
    case "plain":
      // Opt-out: the simple thumbnail (falls back to a cover when there's no image).
      return cover ? (
        <img
          loading="lazy"
          src={cover}
          alt={name}
          className={`rounded-md object-cover ${className ?? ""}`}
          data-vitals-label="thumbnail"
        />
      ) : (
        <BookCover item={resource} theme={theme} className={className} />
      )
    default:
      // "cover": a book cover — but fall back to a spine when the image is off (matches the shelf).
      if (showImage === false) {
        return <BookSpine name={name} subheader={resource.translation} theme={theme} className={className} />
      }
      return <BookCover item={resource} theme={theme} className={className} />
  }
}

export default MediaPreview
